/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import CTALink from '@ion/dls/src/components/buttons/CTALink';
import TabView from '@ion/dls/src/components/tabs/TabView';
import Render from '@ion/dls/src/render/Render';
import {toggleThemeStyle} from '@ion/dls/src/theme/helpers';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import withSignals from '../../components/hoc/withSignals';
import {headerTab, sideTab} from './store';
import {Headertabs, SideNavTabs} from './types';

/**
 * Props for DashboardNavHeader component.
 */
export interface DashboardNavHeaderProps {
  selected?: { title: string; [key: string]: any };
  onBack: () => void;
  onTabChange: (tab: SideNavTabs) => void;
};

const useStyles = makeStyles((theme) => ({
  navHeader : toggleThemeStyle({
    boxShadow      : '1px 2px 5px 0 rgba(0,0,0,0.1)',
    minHeight      : '94px',
    alignContent   : 'center',
    display        : 'flex',
    flexDirection  : 'column',
    justifyContent : 'space-between'
  }, {
    display        : 'flex',
    minHeight      : '94px',
    alignContent   : 'center',
    borderBottom   : `1px solid ${dlsColor('grey5')}`,
    flexDirection  : 'column',
    justifyContent : 'space-between'
  }),
  activeCrumb : {
    color : dlsColor('caBlue')
  },
  inactiveCrumb : {
    color     : dlsColor('grey3'),
    '&:hover' : {
      cursor : 'pointer'
    }
  },
  breadcrumbRoot : toggleThemeStyle({
    paddingLeft : '16px',
    height      : '100%',
    minHeight   : '48px'
  }, {
    display        : 'flex',
    padding        : '8px 16px 8px 16px',
    alignItems     : 'center',
    borderBottom   : `2px solid ${dlsColor('pastelTeal')}`,
    justifyContent : 'space-between'
  }),
  breadcrumb : {
    boxShadow    : '1px 2px 5px 0 rgba(0,0,0,0.1)',
    marginBottom : '4px'
  },
  tab : toggleThemeStyle({
    width : 'fit-content'
  }, {
    gap          : '16px',
    display      : 'flex',
    borderBottom : `2px solid ${dlsColor('pastelTeal')}`
  }),
  ctaLink : {
    ...dlsTypography('cta'),
    color         : dlsColor('caBlue'),
    cursor        : 'pointer',
    letterSpacing : '1px',
    fontSize      : '13px',
    padding       : '12px',
    display       : 'block',
    '&&:hover'    : {
      textDecoration : 'underline'
    }
  },
  heading : {
    color      : dlsColor('grey1'),
    padding    : '8px 0',
    fontSize   : '16px',
    fontFamily : 'Inter',
    fontWeight : 600,
    lineHeight : '20px'
  }
}));

/**
 * Returns tab style overrides used in TabView.
 */
const tabStyles = () => ({
  backgroundStyle : toggleThemeStyle({
    color           : dlsColor('grey3'),
    backgroundColor : dlsColor('white'),
    fontSize        : '12px'
  }, {
    fontSize        : '14px',
    fontFamily      : 'Roboto',
    letterSpacing   : '0.5px',
    textTransform   : 'capitalize',
    backgroundColor : dlsColor('white')
  }),
  selected : toggleThemeStyle({
    color : `${dlsColor('caBlue')} !important`
  }, {
    color : `${dlsColor('darkTeal')} !important`
  }),
  customTabIndicator : toggleThemeStyle({
    backgroundColor : `${dlsColor('caBlue')} !important`
  }, {
    backgroundColor : `${dlsColor('darkTeal')} !important`
  }),
  tabContainer : {
    justifyContent : 'flex-start'
  }
});

/**
 * DashboardNavHeader is the navigation header section used in the side drawer.
 * It shows breadcrumb navigation and a tab switcher (Basic/Advanced).
 *
 * ```typescript
 * <DashboardNavHeader selected={{ title: 'Sales Report' }}
 *   onBack={() => navigateBack()}
 *   onTabChange={(tab) => handleTabSwitch(tab)} />
 * ```
 */
const DashboardNavHeader = ({
  selected,
  onBack,
  onTabChange
}: DashboardNavHeaderProps) => {
  const classes  = useStyles();

  const {
    isLegacy  : isLegacyTheme,
    isCurrent : isCurrentTheme
  } = useThemeContext();

  const handleChange = useCallback((_event: React.ChangeEvent<{}>, newValue: SideNavTabs) => {
    onTabChange(newValue);
  }, [onTabChange]);

  const tabs = useMemo(() => ([{
    tabHeader : translate('sisense:analytics.sideNav.tabs.basic'),
    tab       : SideNavTabs.Basic
  }, {
    tabHeader : translate('sisense:analytics.sideNav.tabs.advanced'),
    tab       : SideNavTabs.Advanced
  }, {
    tabHeader : translate('sisense:analytics.topNavigation.tabs.myReports'),
    tab       : SideNavTabs.MyReports
  }]), []);

  return (
    <div className={classes.navHeader}>
      <Render.If condition={isLegacyTheme}>
        <div className={classes.breadcrumb}>
          <CTALink className={classes.ctaLink}
            onClick={onBack}>
            {translate('sisense:analytics.sideNav.breadcrumbs.root')}
          </ CTALink>
        </div>
      </Render.If>
      <Render.If condition={isCurrentTheme}>
        <div className={classes.breadcrumbRoot}>
          <div className={classes.heading}>
            {translate('sisense:analytics.sideNav.breadcrumbs.root')}
          </div>
        </div>
      </Render.If>
      <div className={classes.tab}>
        <TabView tabItems={tabs}
          onChange={handleChange}
          variant='standard'
          value={sideTab.value}
          overrides={tabStyles}
          passSelected />
      </div>
    </div>
  );
};

export default withSignals(DashboardNavHeader);
